'use client';

import {type FC, useMemo} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import clsx from 'clsx';

import type {SearchPagesResponse} from '@/app/(frontend)/api/search/route';
import {Spinner} from '@/components/spinner';

import {SearchItem} from './search-item';

interface Props {
	extraFields?: string;
	hits: {_type: string; objectID: string}[];
}

export const SearchResults: FC<Props> = ({hits}) => {
	const algoliaIds = useMemo(() => {
		return hits.map((hit) => hit.objectID);
	}, [hits]);

	const fetchPages = async () => {
		if (algoliaIds.length === 0) {
			return {
				searchResults: [],
				totalCount: 0,
			};
		}

		const res = await fetch('/api/search', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(algoliaIds),
		});

		const body: SearchPagesResponse = await res.json();

		return body;
	};

	const {isPending, isError, error, data, isFetching} = useQuery({
		queryKey: ['searchResults', algoliaIds, fetchPages],
		queryFn: () => fetchPages(),
		placeholderData: keepPreviousData,
	});

	const sortDataByAlgoliaIds = useMemo(() => {
		const sortedData = data?.searchResults.sort((a, b) => {
			return algoliaIds.indexOf(a._id) - algoliaIds.indexOf(b._id);
		});
		return sortedData;
	}, [data?.searchResults, algoliaIds]);

	return (
		<div>
			<div className={clsx('mb-8')}>
				<div>
					{(isPending || isFetching) && (
						<div
							className={clsx(
								'flex',
								'items-center',
								'gap-2',
								'my-12',
							)}
						>
							<Spinner aria-hidden="true" /> Laster resultater
						</div>
					)}
					{isError && <div>Error: {error.message}</div>}
					{sortDataByAlgoliaIds && (
						<ul>
							{sortDataByAlgoliaIds.map((result, index) => {
								return (
									<SearchItem key={index} result={result} />
								);
							})}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
};
